<script setup lang="ts">
import { reactive, watch } from "vue";
import { ValidationArgs } from "@vuelidate/core";

import { Suggestion } from "../../entities/suggestion";
import Input from "./Input.vue";

type State = {
    isOpen: boolean;
    setResult: boolean;
}

export interface Props {
    suggestions: Suggestion[],
    notFoundMessage: string,
    notFoundSuggestion?: Suggestion,
    clearInputAfterClick: boolean,
    placeholder?: string,
    id: string,
    validation?: ValidationArgs,
}

const state = reactive<State>({
    isOpen: false,
    setResult: false,
});

const searchTerm = defineModel({ type: String, default: "" });

const props = defineProps<Props>();

const emit = defineEmits<{
    (on:"update:suggestion", value: string): void
    (on:"click:suggestion", value: Suggestion): void
}>();

function setResult(suggestion: Suggestion) {
    state.isOpen = false;
    state.setResult = true;
    searchTerm.value = props.clearInputAfterClick ? "" : suggestion.name;
    emit("click:suggestion", suggestion);
}

watch(() => searchTerm.value, (value) => {
    emit("update:suggestion", searchTerm.value ?? "");
    if (state.setResult || value === "") {
        state.isOpen = false;
        state.setResult = false;
    } else {
        state.isOpen = true;
    }
});

</script>
<template>
    <Input
        :id="props.id"
        v-model="searchTerm"
        :placeholder="props.placeholder"
        type="text"
        class="col-md-12"
        :validation="validation"
    />
    <div v-if="state.isOpen && props.suggestions">
        <button
            v-if="props.suggestions.length === 0 && notFoundSuggestion"
            type="button"
            data-testid="addNotListed"
            class="btn btn-primary w-100 mt-3"
            @click="notFoundSuggestion ? setResult(notFoundSuggestion) : null"
        >
            {{ props.notFoundMessage }}
        </button>
        <ul
            v-else
            class="suggestions sessioncamhidetext"
        >
            <li
                v-for="(suggestion, key) in props.suggestions"
                :key="key"
                tabindex="0"
                class="suggestion suggestion-success"
                @click="setResult(suggestion)"
                @keyup.enter="setResult(suggestion)"
            >
                {{ suggestion.name ?? "" }}
            </li>
            <li
                v-if="props.suggestions.length === 0 && !notFoundSuggestion"
                tabindex="0"
                class="suggestion text-center"
            >
                {{ props.notFoundMessage }}
            </li>
        </ul>
    </div>
</template>
<style>
    .suggestions {
        list-style: none;
        padding: 0;
        margin: 0;
        display: block;
        outline: 0;
        border: 1px solid #ddd;
        background: #fff;
        color: #333;
        max-height: 180px;
        overflow: auto;

    }
    .suggestion {
        margin: 0;
        padding: 10px;
        cursor: pointer;
    }
    .suggestion:hover {
        background-color: #137a8c!important;
        border-color: #137a8c;
        color: #ffffff;
    }
</style>
